import { Avatar, RichEditor } from "components/core";
import { FC, useEffect, useMemo, useRef } from "react";

import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/outline";
import { FormikProps } from "formik";
import { Offer_messages_offer_messages } from "_graphql/queries/__generated__/Offer_messages";
import clsx from "clsx";
import moment from "moment";
import { useCurrentUser } from "_graphql/cache/auth";
import { OfferFrom } from "_graphql/__generated__/globalTypes";

type Props = {
    offers: (Offer_messages_offer_messages | null)[];
    form: FormikProps<any>;
};

const MessageView: FC<Props> = ({ offers, form }) => {
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const currentUser = useCurrentUser();

    const currentUserName = useMemo(() => {
        const first = currentUser?.employee?.employee_first_name || "";
        const last = currentUser?.employee?.employee_last_name || "";
        return [first, last].join(" ").trim().toLowerCase();
    }, [currentUser]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "end",
        });
    };

    useEffect(() => {
        scrollToBottom();
    }, [offers]);

    return (
        <div className="flex flex-col flex-1 h-[500px] overflow-clip">
            <div className="flex h-full flex-1 justify-between overflow-y-scroll no-scrollbar flex-col">
                {offers?.length < 1 ? (
                    <div className="flex-1 h-full flex flex-col justify-center items-center space-y-2">
                        <ChatBubbleLeftEllipsisIcon className="h-12 w-12 text-gray-400" />
                        <p className="text-gray-500 font-medium">
                            No messages yet
                        </p>
                    </div>
                ) : (
                    <div className="flex-1 overflow-y-auto no-scrollbar">
                        <ul className="space-y-3 px-3">
                            {offers.map((activityItem) => {
                                const senderName = (
                                    activityItem?.sender_name || ""
                                ).trim();
                                const isMine =
                                    senderName.toLowerCase() ===
                                        currentUserName ||
                                    (activityItem?.state ===
                                        OfferFrom.PLACEIT_LITE &&
                                        Boolean(currentUser?.employee));

                                return (
                                    <li
                                        key={activityItem?.uuid}
                                        className={clsx(
                                            "flex",
                                            isMine
                                                ? "justify-end"
                                                : "justify-start",
                                        )}>
                                        <div
                                            className={clsx(
                                                "flex items-end max-w-[85%] space-x-2",
                                                isMine
                                                    ? "flex-row-reverse space-x-reverse"
                                                    : "flex-row",
                                            )}>
                                            {!isMine && (
                                                <Avatar
                                                    alt={senderName}
                                                    size="sm"
                                                    color={
                                                        activityItem?.state ===
                                                        "EXTERNAL"
                                                            ? "blue"
                                                            : "green"
                                                    }
                                                />
                                            )}
                                            <div
                                                className={clsx(
                                                    "rounded-2xl px-3 py-2 shadow-sm",
                                                    isMine
                                                        ? "bg-primary-600 text-white rounded-br-none"
                                                        : "bg-gray-100 text-gray-800 rounded-bl-none",
                                                )}>
                                                {!isMine && (
                                                    <div className="text-[11px] font-medium opacity-80 mb-0.5">
                                                        {senderName}
                                                    </div>
                                                )}
                                                <div
                                                    className={clsx(
                                                        "prose-sm break-words",
                                                        isMine
                                                            ? "prose-invert"
                                                            : "",
                                                    )}>
                                                    <p
                                                        dangerouslySetInnerHTML={{
                                                            __html:
                                                                activityItem?.message ||
                                                                "",
                                                        }}
                                                    />
                                                </div>
                                                <div
                                                    className={clsx(
                                                        "mt-1 text-[10px]",
                                                        isMine
                                                            ? "text-white/80 text-right"
                                                            : "text-gray-500",
                                                    )}>
                                                    {moment(
                                                        activityItem?.timestamp,
                                                    ).calendar(null, {
                                                        sameDay:
                                                            "[Today at] HH:mm A",
                                                        lastDay:
                                                            "[Yesterday at] HH:mm A",
                                                        lastWeek:
                                                            "dddd [at] HH:mm A",
                                                        nextWeek:
                                                            "dddd [at] HH:mm A",
                                                        sameElse:
                                                            "DD/MM/YYYY [at] HH:mm A",
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                        <div ref={messagesEndRef} />
                    </div>
                )}
            </div>
            <div className="flex w-full pt-4 border-t ">
                <RichEditor
                    id="message"
                    label=""
                    {...form}
                    // className="w-full focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Type your message here..."
                />
            </div>
        </div>
    );
};

export default MessageView;
